import { lazy, useState } from 'react';
import { useCacheDataController } from 'Context/contexts/CacheDataContext';
import { setDrawerComponent } from 'Context/contexts/cacheData/SideDrawer';
import {
	finopsExecutiveAddOrUpdateInsight,
	finopsExecutiveDeleteInsight,
	finopsExecutiveGetInsights
} from 'service/service';
import { usePepwiseAuthController } from 'Context/contexts/AuthContext';

const extractMonthShort = (monthStr: string): string => {
	if (!monthStr) return '';
	try {
		const [_, month] = monthStr.split('-');
		const monthNamesShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
		                          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
		const monthIndex = parseInt(month, 10) - 1;
		if (monthIndex >= 0 && monthIndex < 12) {
			return monthNamesShort[monthIndex];
		}
	} catch (e) {
		console.error('Error extracting month:', e);
	}
	return '';
};

const extractYear = (monthStr: string): string => {
	if (!monthStr) return '';
	try {
		const [year, _] = monthStr.split('-');
		return year;
	} catch (e) {
		console.error('Error extracting year:', e);
	}
	return '';
};

interface InsightDisplay {
	insightId: number;
	title: string;
	description: string;
	opsType: string;
	createdBy: string;
	updatedBy: string;
	createdDate: string;
	updatedDate: string;
}

interface Insight {
	id?: number;
	title: string;
	description: string;
}

const useFinOpsSideDrawer = () => {
	const [_, dispatch] = useCacheDataController();
	const [authController] = usePepwiseAuthController();
	const [insights, setInsights] = useState<InsightDisplay[]>([]);

	const currentUserEmail = (authController.profile as any)?.mail || '';
	const privilege = authController.privilege;

	const fetchInsights = async (selectedMonth: string, xopsType: string) => {
		try {
			const response = await finopsExecutiveGetInsights(xopsType, '', selectedMonth);
			if (response.success) {
				setInsights(response.data.data);
				return response.data.data;
			}
			return [];
		} catch (error) {
			console.error('Failed to get insights:', error);
			return [];
		}
	};

	const handleAddInsight = async (newInsight: Insight, xopsType: string, month: string) => {
		try {
			const response = await finopsExecutiveAddOrUpdateInsight({
				title: newInsight?.title,
				xopsType: xopsType,
				description: newInsight?.description,
				month: month
			});
			if (response.success) {
				const updatedInsights = await fetchInsights(month, xopsType);
				return updatedInsights;
			}
		} catch (error) {
			console.error('Failed to add insight:', error);
		}
		return insights;
	};

	const handleUpdateInsight = async (updatedInsight: Insight, xopsType: string, month: string) => {
		try {
			const response = await finopsExecutiveAddOrUpdateInsight({
				title: updatedInsight?.title,
				xopsType: xopsType,
				description: updatedInsight?.description,
				insight_id: updatedInsight?.id,
				month: month
			});
			if (response.success) {
				const updatedInsights = await fetchInsights(month, xopsType);
				return updatedInsights;
			}
		} catch (error) {
			console.error('Failed to update insight:', error);
		}
		return insights;
	};

	const handleDeleteInsight = async (insightId: number, xopsType: string, month: string) => {
		try {
			const response = await finopsExecutiveDeleteInsight(insightId);
			if (response.success) {
				const updatedInsights = await fetchInsights(month, xopsType);
				return updatedInsights;
			}
		} catch (error) {
			console.error('Failed to delete insight:', error);
		}
		return insights;
	};

	const handleOpenInsights = async (chartTitle: string, xopsType: string, month: string) => {
		const fetchedInsights = await fetchInsights(month, xopsType);
		const monthShort = extractMonthShort(month);
		const year = extractYear(month);

		const InsightsDrawer = lazy(() => import('./InsightsDrawer'));
		setDrawerComponent(
			dispatch,
			<InsightsDrawer
				title={chartTitle}
				month={monthShort}
				year={year}
				insights={fetchedInsights}
				onAdd={(insight) => handleAddInsight(insight, xopsType, month)}
				onUpdate={(insight) => handleUpdateInsight(insight, xopsType, month)}
				onDelete={(id) => handleDeleteInsight(id, xopsType, month)}
				currentUserEmail={currentUserEmail}
				privilege={privilege}
			/>
		);
	};

	return { handleOpenInsights };
};

export default useFinOpsSideDrawer;
